import { useState, useEffect, ChangeEvent } from 'react';
import { ComputeBudgetProgram, Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { useSplToken } from '../../hooks/useSplToken';
import { useSolana } from '../../hooks/useSolana';
import '../../VolumeWalletControl.css';
import {
    createAssociatedTokenAccountIdempotent,
    createAssociatedTokenAccountIdempotentInstruction,
    createCloseAccountInstruction,
    getAssociatedTokenAddressSync,
    getMint,
} from '@solana/spl-token';
import {
    pumpswapSellUnitLimit,
    volumeSellerPumpswapDefaultJitoTip,
    volumeSellerPumpswapMaxPriorityFeeJitoLamports,
    volumeSellerPumpswapMaxPriorityFeeRPCLamports,
    volumeSellerPumpswapMinPriorityFeeRPCLamports,
    volumeWalletControlMinPriorityFeeJitoLamports,
} from '../../../ConfigFees';
import { usePumpswap } from '../../hooks/usePumpswap';
import { WSOL } from '@raydium-io/raydium-sdk';
import { useJito } from '../../hooks/useJito';
import BN from 'bn.js';
import { useClipboard } from '../../hooks/useClipboard';
import { volumeMakerV2CheckPoolBalanceSolUi } from '../../../ConfigConstants';

export function VolumeSellerPumpswap({
    tokenDeployer,
    volumeBuyWallets,
    connection,
    deployedToken,
    logMessage,
    updatedAt,
    autoAction,
}: {
    tokenDeployer: Keypair;
    volumeBuyWallets: Keypair[];
    connection: Connection;
    deployedToken: string;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    updatedAt: string;
    autoAction: string;
}) {
    const { getMultipleSolBalances } = useSolana();
    const { getWalletsTokenStringBalances } = useSplToken();
    const { getBuyIx, getPoolId, calculateBaseFromQuote, getPoolBaseTokenBalance } = usePumpswap();
    const { copyToClipboard } = useClipboard();
    const { spamAndConfirmBundle } = useJito();

    const [selectedWalletsCount, setSelectedWalletsCount] = useState<number>(0);
    const [selectedWallets, setSelectedWallets] = useState<boolean[]>(new Array(volumeBuyWallets.length).fill(false));
    const [jitoTip, setJitoTip] = useState<number>(volumeSellerPumpswapDefaultJitoTip / LAMPORTS_PER_SOL);
    const [randomWalletsInput, setRandomWalletsInput] = useState<number>(1);
    const [solBalances, setSolBalances] = useState<number[]>(new Array(volumeBuyWallets.length).fill(0));
    const [initialTotalSolBalance, setInitialTotalSolBalance] = useState<number>(0);
    const [totalSolBalance, setTotalSolBalance] = useState<number>(0);
    const [solProfit, setSolProfit] = useState<number>(0);
    const [tokenBalances, setTokenBalances] = useState<number[]>([]);
    const [totalTokenBalance, setTotalTokenBalance] = useState<number>(0);
    const [tokenSupplyPercent, setTokenSupplyPercent] = useState<number>(0);

    const [randomSellLock0, setRandomSellLock0] = useState(false);
    const [randomSellInterval0, setRandomSellInterval0] = useState<number>(1);
    const [randomSellPercent0, setRandomSellPercent0] = useState<number>(100);
    const [randomSellIntervalTimer0, setRandomSellIntervalTimer0] = useState<NodeJS.Timer | null>(null);

    const [randomSellLock1, setRandomSellLock1] = useState(false);
    const [randomSellInterval1, setRandomSellInterval1] = useState<number>(5);
    const [randomSellPercent1, setRandomSellPercent1] = useState<number>(100);
    const [randomSellIntervalTimer1, setRandomSellIntervalTimer1] = useState<NodeJS.Timer | null>(null);

    const [jitoTransaction, setJitoTransaction] = useState<boolean>(false);

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);

    const [slippage, setSlippage] = useState<number>(15);

    useEffect(() => {
        if (!deployedToken) return;
        if (autoAction !== 'volumeSell') return;
        if (autoActionStarted) return;

        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (timeDifference > 30000) return;

        logMessage('Starting auto action for Volume Wallet Control.');
        setAutoActionStarted(true);
        switchRandomSell0();
    }, [deployedToken, autoAction, autoActionStarted]);

    const selectedWalletsChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const newSelectedWallets = [...selectedWallets];
        newSelectedWallets[index] = event.target.checked;
        setSelectedWallets(newSelectedWallets);
    };

    const jitoTipChange = (event: ChangeEvent<HTMLInputElement>) => {
        setJitoTip(Number(event.target.value));
    };

    const randomWalletsInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        let walletsNumber = Number(event.target.value);
        const positiveBalances = tokenBalances.filter((balance) => balance > 0).length;
        if (walletsNumber > positiveBalances) {
            walletsNumber = positiveBalances;
        }
        setRandomWalletsInput(walletsNumber);
    };

    useEffect(() => {
        const fetchBalances = async () => {
            try {
                const allSolBalances = await getMultipleSolBalances(
                    connection,
                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                );
                setSolBalances(allSolBalances);

                const totalSolBalance = Number(allSolBalances.reduce((acc, balance) => acc + balance, 0) / LAMPORTS_PER_SOL);
                setTotalSolBalance(totalSolBalance);

                if (initialTotalSolBalance === 0) {
                    setInitialTotalSolBalance(totalSolBalance);
                } else {
                    const totalProfit = (totalSolBalance - initialTotalSolBalance) / LAMPORTS_PER_SOL;
                    setSolProfit(Number(totalProfit));
                }

                if (!deployedToken || !volumeBuyWallets.length) {
                    return setTokenBalances(new Array(volumeBuyWallets.length).fill('-'));
                }
                console.log('deployedToken', deployedToken);
                const token = await getMint(connection, new PublicKey(deployedToken), 'processed');

                const allTokenBalances = await getWalletsTokenStringBalances(
                    connection,
                    volumeBuyWallets.map((wallet) => wallet.publicKey),
                    new PublicKey(deployedToken),
                );
                setTokenBalances(allTokenBalances.map((balance) => Number(balance) / 10 ** token.decimals));

                const totalTokenBalance = Number(allTokenBalances.reduce((acc, balance) => acc + Number(balance), 0));
                setTotalTokenBalance(totalTokenBalance / 10 ** token.decimals);

                const getTokenSupplyPercent = async () => {
                    const supply = Number(token.supply.toString());
                    const supplyRatio = totalTokenBalance / supply;
                    const percent = supplyRatio * 100;

                    setTokenSupplyPercent(percent);
                };
                getTokenSupplyPercent();
            } catch (error) {
                console.error(error);
            }
        };

        fetchBalances();

        const intervalId = setInterval(fetchBalances, 10000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeBuyWallets, deployedToken]);

    const switchRandomSell0 = async () => {
        if (randomSellLock0) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock0(true);

        if (randomSellIntervalTimer0) {
            clearInterval(randomSellIntervalTimer0);
            setRandomSellIntervalTimer0(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            setRandomSellIntervalTimer0(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            volumeBuyWallets.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], [randomSellPercent0]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval0 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock0(false);
    };

    const switchRandomSell1 = async () => {
        if (randomSellLock1) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock1(true);

        if (randomSellIntervalTimer1) {
            clearInterval(randomSellIntervalTimer1);
            setRandomSellIntervalTimer1(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            logMessage('Searching for AMM pool...');
            setRandomSellIntervalTimer1(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            volumeBuyWallets.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], [randomSellPercent1]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval1 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock1(false);
    };

    useEffect(() => {
        const count = selectedWallets.filter(Boolean).length;
        setSelectedWalletsCount(count);
    }, [selectedWallets]);

    function selectRandomWallets() {
        const randomWallets: number[] = [];
        const totalWallets = volumeBuyWallets.length;

        const positiveBalances = tokenBalances.filter((balance) => balance > 0).length;
        console.log(positiveBalances);
        if (randomWalletsInput > positiveBalances) {
            logMessage('Input cannot be greater than the number of available wallets.', 'error');
            return;
        }

        while (randomWallets.length < randomWalletsInput) {
            const randomIndex = Math.floor(Math.random() * totalWallets);
            if (!randomWallets.includes(randomIndex) && tokenBalances[randomIndex] > 0) {
                randomWallets.push(randomIndex);
            }
        }

        const newSelectedWallets = new Array(totalWallets).fill(false);
        randomWallets.forEach((index) => {
            newSelectedWallets[index] = true;
        });
        setSelectedWallets(newSelectedWallets);
    }

    async function sellMultipleWithPercent(connection: Connection, wallets: Keypair[], percents: number[]) {
        logMessage('Selling...');
        const balances = (
            await getWalletsTokenStringBalances(
                connection,
                wallets.map((wallet) => wallet.publicKey),
                new PublicKey(deployedToken),
            )
        ).map((balance) => Number(balance));

        const swapValues = balances.map((balance, index) => {
            return Math.floor((balance * percents[index]) / 100);
        });

        for (const [index, wallet] of wallets.entries()) {
            sellSingle(connection, wallet, swapValues[index]);
        }
    }

    async function sellSingle(connection: Connection, wallet: Keypair, tokenAmount: number) {
        try {
            const min = jitoTransaction ? volumeWalletControlMinPriorityFeeJitoLamports : volumeSellerPumpswapMinPriorityFeeRPCLamports;
            const max = jitoTransaction ? volumeSellerPumpswapMaxPriorityFeeJitoLamports : volumeSellerPumpswapMaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);

            const poolId = await getPoolId(tokenDeployer.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
            const solAmount = await calculateBaseFromQuote(connection, poolId, new BN(tokenAmount), slippage);

            const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), wallet.publicKey);

            const poolSolBalance = await getPoolBaseTokenBalance(connection, poolId);
            if (Number(poolSolBalance) < volumeMakerV2CheckPoolBalanceSolUi * LAMPORTS_PER_SOL) {
                logMessage(`Pool balance is less than ${volumeMakerV2CheckPoolBalanceSolUi} SOL. Skipping...`, 'error');
                //switchRandomSell();
                return;
            }

            const message = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [
                    createAssociatedTokenAccountIdempotentInstruction(wallet.publicKey, wsolAta, wallet.publicKey, new PublicKey(WSOL.mint)),
                    getBuyIx(
                        poolId,
                        new PublicKey(WSOL.mint),
                        new PublicKey(deployedToken),
                        wallet.publicKey,
                        BigInt(solAmount.toString()),
                        BigInt(tokenAmount),
                    ),
                    createCloseAccountInstruction(wsolAta, wallet.publicKey, wallet.publicKey),
                    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports }),
                ],
            }).compileToV0Message();
            const buyTx = new VersionedTransaction(message);
            buyTx.sign([wallet]);

            const hash = await connection.sendTransaction(buyTx, { skipPreflight: true });
            logMessage(`Transaction sent: ${hash}, token amount ${tokenAmount / Math.pow(10, 9)}`, 'success');
        } catch (e) {
            console.log(e);
            logMessage(`Error selling wallet from ${wallet.publicKey.toBase58()}`);
        }
    }

    return (
        <section>
            <h2>Volume Wallet Control</h2>
            <section className='volume-sell-buy-container'>
                <div className='volume-selection-container'>
                    <p>
                        <span>{selectedWalletsCount}</span> Wallet(s) Selected
                    </p>
                    <div className='jito-tx-check-inp'>
                        <input type='checkbox' checked={jitoTransaction} onClick={() => setJitoTransaction((prev) => !prev)} />
                        <p>Jito TX</p>
                    </div>
                    <label>
                        JITO Tip
                        <input type='number' value={jitoTip} onChange={jitoTipChange} />
                    </label>
                    <label>
                        Slippage
                        <input type='number' value={slippage} onChange={(e) => setSlippage(Number(e.target.value))} />
                    </label>
                </div>
                <div className='volume-sell-buttons-container'>
                    <p>SELL</p>
                    <div>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(100),
                                );
                            }}
                        >
                            100%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(75),
                                );
                            }}
                        >
                            75%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(50),
                                );
                            }}
                        >
                            50%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(20),
                                );
                            }}
                        >
                            20%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(10),
                                );
                            }}
                        >
                            10%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(5),
                                );
                            }}
                        >
                            5%
                        </button>
                        <button
                            onClick={async () => {
                                sellMultipleWithPercent(
                                    connection,
                                    volumeBuyWallets.filter((wallet, index) => selectedWallets[index]),
                                    new Array(selectedWalletsCount).fill(1),
                                );
                            }}
                        >
                            1%
                        </button>
                    </div>
                </div>
            </section>
            <section className='volume-control-buttons-container'>
                <section className='interval-ref-selrandom'>
                    <div className='enable-sec-percent'>
                        <div className='inp'>
                            <label className='switch'>
                                <input
                                    type='checkbox'
                                    checked={randomSellIntervalTimer0 != null}
                                    onClick={switchRandomSell0}
                                    disabled={!deployedToken || volumeBuyWallets.length === 0}
                                />
                                <span className='slider'></span>
                            </label>
                        </div>

                        <div className='interval'>
                            <input
                                type={'number'}
                                value={randomSellInterval0}
                                onChange={(event) => setRandomSellInterval0(parseInt(event.target.value) || 5)}
                                disabled={randomSellIntervalTimer0 != null}
                            />
                            sec
                        </div>
                        <div className='percent'>
                            <input
                                type={'number'}
                                value={randomSellPercent0}
                                onChange={(event) => setRandomSellPercent0(parseInt(event.target.value) || 10)}
                                disabled={randomSellIntervalTimer0 != null}
                            />
                            %
                        </div>
                    </div>
                    <div className='volume-select-random-button'>
                        <button className='button-accent' onClick={selectRandomWallets}>
                            SELECT RANDOM &nbsp; &nbsp; &nbsp;
                            <input type='number' value={randomWalletsInput} onChange={randomWalletsInputChange} className='input-on-button' /> &nbsp; WALLETS
                        </button>
                    </div>
                </section>
                <br />
                <section className='ref-sel-all'>
                    <div className='enable-sec-percent'>
                        <div className='inp'>
                            <label className='switch'>
                                <input
                                    type='checkbox'
                                    checked={randomSellIntervalTimer1 != null}
                                    onClick={switchRandomSell1}
                                    disabled={!deployedToken || volumeBuyWallets.length === 0}
                                />
                                <span className='slider'></span>
                            </label>
                        </div>

                        <div className='interval'>
                            <input
                                type={'number'}
                                value={randomSellInterval1}
                                onChange={(event) => setRandomSellInterval1(parseInt(event.target.value) || 5)}
                                disabled={randomSellIntervalTimer1 != null}
                            />
                            sec
                        </div>
                        <div className='percent'>
                            <input
                                type={'number'}
                                value={randomSellPercent1}
                                onChange={(event) => setRandomSellPercent1(parseInt(event.target.value) || 10)}
                                disabled={randomSellIntervalTimer1 != null}
                            />
                            %
                        </div>
                    </div>
                    <div className='volume-control-buttons'>
                        <button className='button-accent'>
                            <div className='icon-ref'></div>
                        </button>
                        <button
                            className='button-accent'
                            onClick={() => setSelectedWallets(new Array(volumeBuyWallets.length).fill(selectedWalletsCount !== volumeBuyWallets.length))}
                        >
                            <div className='icon-sel-all'></div>
                        </button>
                    </div>
                </section>
            </section>
            <div className='home-table-wrapper'>
                <table className='volume-control-table'>
                    <thead>
                        <tr>
                            <th>Volume Wallet</th>
                            <th>Tokens</th>
                            <th>SOL</th>
                            <th>Select</th>
                        </tr>
                    </thead>
                    <tbody>
                        {volumeBuyWallets.map((wallet, index) => (
                            <tr key={index}>
                                <td className='volume-wallets'>
                                    <b>Vol{index + 1}</b> - {wallet.publicKey.toBase58().substring(0, 5)}...
                                    {wallet.publicKey.toBase58().substring(wallet.publicKey.toBase58().length - 5)}
                                    <button className='copy-button copy-wallets-button' onClick={() => copyToClipboard(wallet.publicKey.toBase58())}>
                                        <span className='copy-button-active-effect'></span>
                                    </button>
                                </td>
                                <td>{tokenBalances[index]} </td>
                                <td>{(solBalances[index] / LAMPORTS_PER_SOL).toFixed(2)}</td>
                                <td>
                                    <input type='checkbox' checked={selectedWallets[index] || false} onChange={selectedWalletsChange(index)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td></td>
                            <td>
                                Total:
                                <br />
                                {totalTokenBalance.toLocaleString()}
                                <br />
                                {tokenSupplyPercent.toFixed(3)}% of Supply
                            </td>
                            <td>
                                Total:
                                <br />
                                {totalSolBalance.toFixed(2)} SOL
                                {solProfit ? (
                                    <p>
                                        <span className='sol-profit'>+{solProfit.toFixed(2)} SOL </span>
                                        Profit
                                    </p>
                                ) : (
                                    <p>
                                        <span className='no-sol-profit'>{solProfit.toFixed(2)} SOL </span>
                                        Profit
                                    </p>
                                )}
                            </td>
                        </tr>
                        <tr></tr>
                    </tfoot>
                </table>
            </div>
        </section>
    );
}
